import dayjs from 'dayjs';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BsCalendar2,
  BsClock,
  BsGeoAlt,
  BsPerson,
  BsTags,
} from 'react-icons/bs';

import { Button, ResponsiveModalBottomSheet } from '@/components';
import { type Booking, type TreatmentItem } from '@/models';
import {
  BookingStatusBadge,
  PaymentStatusBadge,
} from '@/pages/components/Badge';
import {
  formatAppointmentDate,
  formatBookingCode,
  formatPrice,
  formatAppointmentTimeRange,
} from '@/utils/format';

interface BookingHistoryDetailBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking | null;
  onReorderClick?: (booking: Booking) => void;
}

const formatStoreDisplayName = (booking: Booking | null): string => {
  if (!booking) return '—';
  const label = [booking.storeName, booking.storeBranchName]
    .filter(Boolean)
    .join(' - ');
  return label || '—';
};

const getItemUnitPrice = (item: TreatmentItem): number => {
  return item.retailPrice > 0 ? item.retailPrice : item.originalPrice;
};

const computeMaxDurationMinutes = (booking: Booking | null): number => {
  if (!booking) return 0;

  let max = 0;
  booking.customers.forEach((customer) => {
    customer.treatmentItems.forEach((item) => {
      // In this project UI, `optionId` is displayed as minutes.
      max = Math.max(max, item.optionId);
    });
  });

  return max;
};

const BookingHistoryDetailBottomSheet: FC<
  BookingHistoryDetailBottomSheetProps
> = ({ isOpen, onClose, booking, onReorderClick }) => {
  const { t } = useTranslation();

  const storeDisplayName = formatStoreDisplayName(booking);
  const dateLabel = formatAppointmentDate(booking?.availabilityId) || '—';
  const guestCount = booking?.customers.length ?? 0;
  const bookingCodeLabel = formatBookingCode(booking?.bookingCode ?? '');
  const promotionDiscount = booking?.promotionDiscount ?? 0;
  const packageDiscount = booking?.packageDiscount ?? 0;
  const subtotalLabel = formatPrice(booking?.subtotal);
  const promotionDiscountLabel = formatPrice(promotionDiscount);
  const packageDiscountLabel = formatPrice(packageDiscount);
  const totalLabel = formatPrice(booking?.total);
  const depositTotal = booking?.depositTotal ?? 0;
  const remainingTotal = booking?.remainingTotal ?? 0;
  const depositTotalLabel = formatPrice(booking?.depositTotal);
  const remainingTotalLabel = formatPrice(booking?.remainingTotal);

  const timeRangeLabel = (() => {
    if (!booking?.availabilityId) return '—';

    const startTime = dayjs(booking.availabilityId);
    const durationMinutes = computeMaxDurationMinutes(booking);
    const endTime =
      durationMinutes > 0 ? startTime.add(durationMinutes, 'minute') : null;

    const label = formatAppointmentTimeRange({
      startTime,
      endTime,
      maxDurationMinutes: durationMinutes,
      hoursShortLabel: t('treatment.hoursShort'),
      minuteLabel: t('common.min'),
    });

    return label || '—';
  })();

  return (
    <ResponsiveModalBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={t('payment.bookingDetails')}
    >
      <div className="flex flex-col gap-4 px-4 pt-2 pb-6">
        <div className="flex items-center gap-1">
          <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
            {t('profile.bookingDetails.bookingCode')}:
          </p>
          <p className="text-primary text-base leading-5 font-normal tracking-[0.08px]">
            {bookingCodeLabel}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <BookingStatusBadge status={booking?.status} />

          <div className="flex items-center gap-2">
            <BsGeoAlt className="text-text-muted size-4" />
            <p className="text-primary-text text-base leading-6 tracking-[0.08px]">
              {storeDisplayName}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <BsCalendar2 className="text-text-muted size-4" />
            <p className="text-primary-text text-base leading-6 tracking-[0.08px]">
              {dateLabel}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <BsClock className="text-text-muted size-4" />
            <p className="text-primary-text text-base leading-6 tracking-[0.08px]">
              {timeRangeLabel}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <BsPerson className="text-text-muted size-4" />
            <p className="text-primary-text text-base leading-6 tracking-[0.08px]">
              {guestCount}
            </p>
          </div>
        </div>

        <hr className="border-divider" />

        <div className="flex flex-col gap-4">
          {(booking?.customers ?? []).map((customer, customerIndex) => {
            const customerLabel =
              customer.name || `${t('payment.customer')} ${customerIndex + 1}`;

            return (
              <div key={customerIndex} className="flex flex-col gap-2">
                <div className="flex h-6 items-center">
                  <p className="text-text-secondary-light text-base leading-5 font-semibold tracking-[0.08px]">
                    {customerLabel}
                  </p>
                </div>

                <div className="flex flex-col gap-1.5">
                  {customer.treatmentItems.map((item, itemIndex) => {
                    const label =
                      item.treatmentName ??
                      t('components.bookingDetails.treatmentFallback', {
                        id: item.treatmentId,
                      });
                    const price = getItemUnitPrice(item);

                    return (
                      <div key={itemIndex} className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-primary-text flex-1 text-base leading-5 tracking-[0.08px]">
                            {label}
                          </p>
                          <p className="text-text-secondary-light shrink-0 text-base leading-5">
                            x 1
                          </p>
                        </div>

                        <div className="flex items-center gap-[11px]">
                          <div className="flex items-center gap-1">
                            <BsClock className="text-text-muted size-3.5" />
                            <p className="text-text-secondary-light text-sm leading-5 tracking-[0.08px]">
                              {item.optionId}{' '}
                              {t('components.bookingDetails.minutesShort')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <BsTags className="text-text-muted size-4" />
                            <p className="text-text-secondary-light text-sm leading-5 tracking-[0.08px]">
                              {formatPrice(price)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {customer.remark?.trim() ? (
                  <p className="text-error text-base leading-5 tracking-[0.08px]">
                    {customer.remark}
                  </p>
                ) : null}

                {customerIndex < (booking?.customers.length ?? 0) - 1 ? (
                  <hr className="border-divider mt-2" />
                ) : null}
              </div>
            );
          })}
        </div>

        <hr className="border-divider" />

        <div className="flex flex-col gap-2">
          <div className="flex h-6 items-center justify-between gap-2">
            <p className="text-text-secondary-light text-base leading-5 font-semibold tracking-[0.08px]">
              {t('profile.bookingDetails.payment')}
            </p>

            <PaymentStatusBadge status={booking?.paymentStatus} />
          </div>

          <div className="flex items-center justify-between">
            <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
              {t('profile.bookingDetails.paymentBreakdown.subtotal')}
            </p>
            <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
              {subtotalLabel}
            </p>
          </div>

          {promotionDiscount > 0 ? (
            <div className="flex items-center justify-between">
              <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
                {t('profile.bookingDetails.paymentBreakdown.discount')}
              </p>
              <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
                {promotionDiscountLabel}
              </p>
            </div>
          ) : null}

          {packageDiscount > 0 ? (
            <div className="flex items-center justify-between">
              <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
                {t('profile.bookingDetails.paymentBreakdown.payWithPackage')}
              </p>
              <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
                {packageDiscountLabel}
              </p>
            </div>
          ) : null}

          <div className="flex items-center justify-between">
            <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
              {t('bookingCostSummary.total')}
            </p>
            <p className="text-primary-text text-lg leading-5 font-semibold">
              {totalLabel}
            </p>
          </div>

          {depositTotal > 0 ? (
            <div className="flex items-center justify-between">
              <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
                {t('profile.bookingDetails.paymentBreakdown.paid')}
              </p>
              <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
                {depositTotalLabel}
              </p>
            </div>
          ) : null}

          {remainingTotal > 0 ? (
            <>
              <div className="flex items-center justify-between">
                <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
                  {t('profile.bookingDetails.paymentBreakdown.remaining')}
                </p>
                <p className="text-primary-text text-base leading-5 tracking-[0.08px]">
                  {remainingTotalLabel}
                </p>
              </div>

              <p className="text-text-disabled text-center text-base leading-5 tracking-[0.08px]">
                {t(
                  'profile.bookingDetails.paymentBreakdown.payRemainingAtStore',
                )}
              </p>
            </>
          ) : !depositTotal ? (
            <div className="h-5" aria-hidden />
          ) : null}
        </div>

        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={() => {
            if (!booking) return;
            onReorderClick?.(booking);
          }}
        >
          {t('profile.home.actions.reorder')}
        </Button>
      </div>
    </ResponsiveModalBottomSheet>
  );
};

export default BookingHistoryDetailBottomSheet;
